import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { PAGE_SIZE, formatDate } from '../lib/format';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import { FilterSubmitButton } from '../components/FilterSubmitButton';

export default function AppUsers() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emailContains, setEmailContains] = useState('');
  const [phoneContains, setPhoneContains] = useState('');
  const [nameContains, setNameContains] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { skip, limit };
      const e = emailContains.trim();
      const p = phoneContains.trim();
      const n = nameContains.trim();
      if (e) params.emailContains = e;
      if (p) params.phoneContains = p;
      if (n) params.nameContains = n;
      const raw = await adminApi.listAppUsers(params);
      const norm = normalizeListResponse(raw, limit);
      setRows(norm.items);
      setTotalCount(norm.totalCount);
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [skip, limit, emailContains, phoneContains, nameContains]);

  useEffect(() => {
    load();
  }, [load]);

  function applyFilters() {
    setSkip(0);
  }

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (r) => r.name || r.fullName || '—',
    },
    {
      key: 'email',
      label: 'Email',
      render: (r) => r.email || '—',
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (r) => r.phone || r.phoneNumber || '—',
    },
    {
      key: 'created',
      label: 'Joined',
      render: (r) => formatDate(r.createdAt || r.created_at),
    },
    {
      key: 'actions',
      label: '',
      width: '100px',
      render: (r) => {
        const id = r.userId || r.id || r._id;
        return id ? (
          <Link to={`/app-users/${id}`} className="btn btn-sm btn-outline-primary">
            View
          </Link>
        ) : (
          '—'
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title="App users" subtitle="Customer accounts" />
      <ErrorBanner message={error} />
      <Card className="mb-3">
        <CardBody>
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label className="form-label small mb-1">Email contains</label>
              <input className="form-control" value={emailContains} onChange={(ev) => setEmailContains(ev.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">Phone contains</label>
              <input className="form-control" value={phoneContains} onChange={(ev) => setPhoneContains(ev.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">Name contains</label>
              <input className="form-control" value={nameContains} onChange={(ev) => setNameContains(ev.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1 text-transparent user-select-none" aria-hidden="true">
                Filter
              </label>
              <FilterSubmitButton onClick={applyFilters} />
            </div>
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardBody className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" />
            </div>
          ) : (
            <DataTable columns={columns} rows={rows} rowKey={(r, i) => r.userId || r.id || r._id || i} />
          )}
          <Pagination totalCount={totalCount} skip={skip} limit={limit} onChange={({ skip: s }) => setSkip(s)} />
        </CardBody>
      </Card>
    </>
  );
}
